import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  prepareGuestData,
  saveCbtGuestsForFuture,
  updateCbtGuestsForFuture,
} from "shared/cbt/api/guests";
import {
  BOOKING_MAP,
  getCountryCodeFromTelephoneNumber,
  getIndex,
} from "shared/containers/Result/ResultContainer";
import { setBookingFormValue } from "shared/data/actions/bookingForm";
import { resetGuestsAndTravellers } from "shared/data/actions/cbt";
import {
  startUniversalLoading,
  stopUniversalLoading,
} from "shared/data/actions/loader";
import {
  assureCorrectCustomerToken,
  getCbtToken,
} from "shared/data/customerTokens";
import {
  setLanguage,
  setReservationsInfo,
} from "shared/data/persistentStorage";
import { AppState } from "shared/data/reducers";
import golApi from "shared/gol-api";
import bookReservationsConfirmRequest1 from "shared/gol-api/bookReservationsConfirm";
import { validateField } from "shared/hooks/bookingFormElement";
import calcFullPrice from "shared/lib/calcFullPrice";
import { capitalizeFirstLetter } from "shared/lib/functions";
import { ResponseObject } from "shared/lib/golObjectTypes/ResponseObject";

import { getArrayRefsFromFlatRefs } from "../lib/helperFunctions";
import Logger from "../services/Logger";

export interface BookingFormSubmitProps {
  refs: string;
  fullPrice: string;
  scrollToError?: Function;
  navigation: any;
}

interface BookedReservation {
  ReservationId: number;
  ReservationParts: {
    ReservationPart: {
      PartId?: number;
    }[];
  };
  Customer: {
    ContactInfo?: {
      Email?: {
        $t: string;
      };
    };
  };
  FlightPrice?: {
    FullPrice: number;
  };
}

interface BookingData {
  BookedReservations?: {
    BookedReservation: BookedReservation[];
  };
}

interface Reservation {
  reservationIdInternal: number;
  reservationId: number;
  reservationPartId: number;
  reservationEmail: string;
  reservationFullPrice?: number;
}

interface ReservationInfo {
  reservations: Reservation[];
}

type PriceReservationType = { ReservationId: string; Price: string }[];
type FireGtmEvents = (bookingData) => void;

export function useBookingFormSubmit({
  refs,
  fullPrice,
  onSubmitError,
  redirectToBooked,
  showCustomError,
  navigateToPayment,
  isHotel,
  onCustomErrorClose,
  fireGtmEvents = (() => {}) as FireGtmEvents,
  alternativeCurrency = null,
}) {
  const [unconfirmedReservations, setUnconfirmedReservations] = useState(null);
  const [bookingResponse, setBookingResponse] = useState(null);
  const [summaryPrices, setSummaryPrices] = useState(null);
  const [
    alternativeCurrencySummaryPrices,
    setAlternativeCurrencySummaryPrices,
  ] = useState(null);
  const [currency, setCurrency] = useState<null | string>(null);
  const [isSendingBookingForm, setIsSendingBookingForm] = useState(true);

  const {
    isConditionsConfirmed,
    paymentTypeId,
    bookingForm,
    bookingFormFields,
    bookingFormSections,
    ticketingCombinations,
    promoCode,
    codeBook,
    guestsToCreateInDynamicForm,
    guestsToUpdateInDynamicForm,
    cbtApiUrl,
    customerUsername,
    guestsCache,
    guestsUsedInDynamicForm,
    showValidationErrors,
    paymentFormMethods,
    currentLanguage,
    cbt,
    isLoggedIn,
    actualFlightServices,
    isFetchingServices,
    gtmId,
    currencyState,
  } = useSelector((state: AppState) => ({
    isConditionsConfirmed: state.bookingForm.isConditionsConfirmed,
    paymentTypeId: state.bookingForm.paymentTypeId,
    bookingForm: state.bookingForm,
    bookingFormFields: state.bookingForm.bookingFormFields,
    bookingFormSections: state.bookingForm.bookingFormSections,
    ticketingCombinations: state.bookingForm.ticketingCombinations,
    actualFlightServices: state.search.actualFlightServices,
    promoCode: state.bookingForm.promoCode,
    codeBook: state.bookingForm.codeBook,
    guestsToCreateInDynamicForm: state.cbt.guestsToCreateInDynamicForm,
    guestsToUpdateInDynamicForm: state.cbt.guestsToUpdateInDynamicForm,
    isFetchingServices: state.bookingForm.isFetchingServices,
    guestsCache: state.cbt.guestsCache,
    cbtApiUrl:
      state.storage.frontendSettings?.dealerCorporateSettings?.cbtApiUrl,
    customerUsername: state.user.email,
    guestsUsedInDynamicForm: state.cbt.guestsUsedInDynamicForm,
    cbt: state.cbt,
    showValidationErrors: state.bookingForm.showValidationErrors,
    paymentFormMethods:
      state.bookingForm.codeBook?.PaymentformMethods?.PaymentformMethod || [],
    currentLanguage: state.requestorConfig.currentLanguage,
    isLoggedIn: state.user.isLoggedIn,
    gtmId:
      state.storage.frontendSettings?.dealerGoogleSettings
        ?.tagManagerContainerId,
    currencyState: state.price.currency,
  }));

  const setInitState = async () => setIsSendingBookingForm(false);

  useEffect(() => {
    setInitState();
  }, []);

  const dispatch = useDispatch();

  const toggleIsConditionsConfirmed = () => {
    dispatch(
      setBookingFormValue("isConditionsConfirmed", !isConditionsConfirmed)
    );
  };

  const onSubmit = async (bookingData = null, bookingCurrency = null) => {
    setIsSendingBookingForm(true);

    try {
      if (bookingData && bookingCurrency) {
        dispatch(startUniversalLoading());
        await finishReservation(bookingData, bookingCurrency);
      } else {
        await sendBookingForm();
      }
    } catch (e) {
      Logger.error("Failed to finish or pay the reservation: ", e);
      dispatch(stopUniversalLoading());
      return false;
    } finally {
      setIsSendingBookingForm(false);
    }
  };
  async function sendBookingForm() {
    if (!(await canSendBookingForm())) {
      dispatch(setBookingFormValue("showValidationErrors", true));
      onSubmitError();
      setIsSendingBookingForm(false);
      return false;
    }

    dispatch(startUniversalLoading());
    const bookingRequest = await buildBookingRequest();

    await assureCorrectCustomerToken({ rewrite: !isLoggedIn });
    const booking: ResponseObject = await golApi.bookReservations(
      bookingRequest,
      alternativeCurrency
    );

    if (!booking.success && booking.errorMsg) {
      showCustomError({
        messageText: booking.errorMsg,
        onClose: () => onCustomErrorClose(booking.errorCode),
      });
      dispatch(stopUniversalLoading());
      setIsSendingBookingForm(false);
      return false;
    }
    if (!booking.data?.UnconfirmedReservations) {
      await finishReservation(booking.data, booking.currency);
    } else {
      setUnconfirmedReservations(booking.data.UnconfirmedReservations);
      setSummaryPrices(booking.data?.DisplaySummaryPrice);
      setAlternativeCurrencySummaryPrices(
        booking.data?.AlternativeCurrencyDisplaySummaryPrice
      );
      setBookingResponse(booking.data);
      setCurrency(booking.currency);
      setIsSendingBookingForm(false);
      dispatch(stopUniversalLoading());
    }
  }

  async function canSendBookingForm() {
    if (!validateAllFields()) {
      return false;
    }

    if (!paymentTypeId) {
      showCustomError({ messageId: "Result.choosePaymentMethod" });
      return false;
    }

    if (!isConditionsConfirmed && !isHotel) {
      showCustomError({ messageId: "Alert.acceptConditions" });
      return false;
    }

    return true;
  }

  async function createCbtGuests() {
    if (
      !guestsToCreateInDynamicForm ||
      guestsToCreateInDynamicForm.length === 0
    ) {
      return;
    }

    await saveCbtGuestsForFuture({
      guestsData: prepareGuestData({
        forms: bookingFormFields,
        cbtGuestsIndexes: guestsToCreateInDynamicForm,
      }),
      cbtApiUrl,
      customerUsername,
      cbtToken: await getCbtToken(),
    });
  }

  async function updateCbtGuests() {
    if (
      !guestsToUpdateInDynamicForm ||
      guestsToUpdateInDynamicForm.length === 0
    ) {
      return;
    }

    await updateCbtGuestsForFuture({
      guestsData: prepareGuestData({
        forms: bookingFormFields,
        cbtGuestsIndexes: guestsToUpdateInDynamicForm,
      }),
      cbtApiUrl,
      customerUsername,
      cbtToken: await getCbtToken(),
      cbtGuestsIndexes: guestsToUpdateInDynamicForm,
      guestsUsedInDynamicForm,
      guestsCache,
    });
  }

  async function finishReservation(bookingData, currencyOfReservation) {
    await createCbtGuests();
    await updateCbtGuests();

    await saveReservationInfoToPersistentStorage(bookingData);
    await setIsSendingBookingForm(true);

    if (gtmId) {
      await fireGtmEvents(bookingData);
    }

    await dispatch(resetGuestsAndTravellers());

    if (isHotelReservation(bookingData)) {
      return redirectToBooked({
        reservationsIds:
          bookingData.BookedReservations.BookedReservation[0].ReservationId,
        email:
          bookingData.BookedReservations.BookedReservation[0].Customer
            .ContactInfo.Email.$t,
        currency: currencyOfReservation,
        paymentAdditionalPrice:
          Number(bookingData.TicketingCombination?.PaymentformOption?.Price) ||
          0,
      });
    }

    if (
      getTypePayment(bookingData) === "ONLINE" &&
      bookingData?.BookedReservations?.BookedReservation[0]?.PaymentStatus !==
        "UNAPPROVED"
    ) {
      return await makePayment(bookingData);
    }

    return redirectToBooked({
      reservationsIds: bookingData.BookedReservations.BookedReservation.map(
        (reservation) => reservation.ReservationId
      ).join(","),
      email:
        bookingData.BookedReservations.BookedReservation[0].Customer.ContactInfo
          .Email.$t,
      currency: currencyOfReservation,
      paymentAdditionalPrice: calcFullPrice([
        Number(
          bookingData?.BookedReservations?.BookedReservation[0]
            ?.TicketingCombination?.PaymentformOption?.Price
        ) || 0,
        Number(
          bookingData?.BookedReservations?.BookedReservation[1]
            ?.TicketingCombination?.PaymentformOption?.Price
        ) || 0,
      ]),
    });
  }

  async function saveReservationInfoToPersistentStorage(bookingData) {
    const { reservations } = getReservationInfoFromBookingData(bookingData);

    setReservationsInfo(reservations);
    setLanguage(currentLanguage);
  }

  function getReservationInfoFromBookingData(
    bookingData: BookingData
  ): ReservationInfo {
    const reservations: Reservation[] = (
      bookingData?.BookedReservations?.BookedReservation || []
    ).map((bookedReservation: BookedReservation) => {
      const reservationIdInternal = bookedReservation.ReservationId;
      const reservationId = bookedReservation.ReservationId;
      const reservationPartId =
        bookedReservation.ReservationParts?.ReservationPart[0]?.PartId ??
        reservationId;
      const reservationEmail =
        bookedReservation.Customer?.ContactInfo?.Email?.$t;
      const reservationFullPrice = bookedReservation.FlightPrice?.FullPrice;

      return {
        reservationId,
        reservationPartId,
        reservationEmail,
        reservationFullPrice,
        reservationIdInternal,
      };
    });

    return {
      reservations,
    };
  }

  async function makePayment(bookingData) {
    const paymentUrl = await getPaymentUrl(bookingData);

    return await navigateToPayment(paymentUrl);
  }

  function buildPriceReservationData(bookingData): PriceReservationType {
    let priceReservations: PriceReservationType = [];

    bookingData.BookedReservations.BookedReservation.forEach((reservation) => {
      const addReservation = {
        ReservationId: reservation.ReservationId,
        Price: reservation.FlightPrice?.FullPrice,
      };

      priceReservations = [...priceReservations, addReservation];
    });

    return priceReservations;
  }

  async function getPaymentUrl(bookingData) {
    const resultUrl: ResponseObject = await golApi.reservationPayments(
      buildPriceReservationData(bookingData),
      getPaymentformOptionFromBookingDataAndAdditionalPaymentData(
        bookingData.TicketingCombination?.PaymentformOption ??
          bookingData.BookedReservations?.BookedReservation[0]
            ?.TicketingCombination.PaymentformOption
      ),
      bookingData.TicketingCombination?.TicketplaceOption ??
        bookingData.BookedReservations?.BookedReservation[0]
          ?.TicketingCombination.TicketplaceOption
    );

    if (!resultUrl.success) {
      dispatch(stopUniversalLoading());

      if (resultUrl.errorMsg) {
        showCustomError({ messageText: resultUrl.errorMsg });
      }

      return false;
    }

    return resultUrl.data.GolPayLink.$t;
  }

  function getPaymentformOptionFromBookingDataAndAdditionalPaymentData(
    paymentFormOption
  ) {
    const paymentformMethodForms =
      codeBook.PaymentformMethodForms?.PaymentformMethodForm || [];

    // @ts-ignore
    if (!paymentformMethodForms?.length > 0 || paymentTypeId === null) {
      return paymentFormOption;
    }

    const selectedPaymentMethod = paymentFormMethods.find(
      (paymentMethod) => paymentMethod.Id === paymentTypeId
    )?.Method;

    if (!selectedPaymentMethod) {
      return paymentFormOption;
    }

    const applicablePaymentMethodForm = paymentformMethodForms.find(
      (paymentMethodForm) => paymentMethodForm.Method === selectedPaymentMethod
    );

    if (!applicablePaymentMethodForm) {
      return paymentFormOption;
    }

    const bookingGroupPaymentFormMethod = buildBooking(
      applicablePaymentMethodForm.BookingFormGroup[0],
      { approvalParams: {}, fields: bookingFormFields, isHotel }
    );

    const resultPaymentFormOption = {
      ...paymentFormOption,
      Parameters: {
        ParameterGroup: {
          Code: bookingGroupPaymentFormMethod.Code,
          ParameterElement: bookingGroupPaymentFormMethod.ParameterElement,
        },
      },
    };

    return resultPaymentFormOption;
  }

  function processManagerApprovalParams(fields, managedTravellers) {
    return Object.keys(fields).reduce((acc, curr) => {
      if (!curr.includes("-manager")) {
        return acc;
      }
      const travellerWithManagerIdx = curr.substring(0, curr.indexOf("-"));
      const managerId = fields[curr].value;
      const travellerEmailKey = Object.keys(fields).find((key) =>
        [
          `${travellerWithManagerIdx}-passenger_email`,
          `${travellerWithManagerIdx}-traveler_email`,
        ].includes(key)
      );
      const travellerEmail = fields[travellerEmailKey].value;
      const travellerType = managedTravellers.travellers.find(
        ({ Email }) => Email === travellerEmail
      )
        ? "Traveller"
        : "Guest";
      const travellerId =
        travellerType === "Traveller"
          ? managedTravellers.travellers.find(
              ({ Email }) => Email === travellerEmail
            ).TravellerId
          : null;

      const basicResponse = {
        travellerType,
        travellerEmail,
        managerId,
      };
      const travellerIdResponse = travellerId ? { travellerId } : {};
      return [...acc, { ...basicResponse, ...travellerIdResponse }];
    }, []);
  }

  async function buildBookingRequest() {
    const { policyResolvedId, usersCache } = cbt;

    let approvalParams: any = policyResolvedId
      ? { policyResolvedId, authToken: await getCbtToken() }
      : {};

    const travellerManagers = processManagerApprovalParams(
      bookingFormFields,
      bookingForm.managedTravellers
    );

    if (travellerManagers && travellerManagers.length > 0) {
      approvalParams = {
        ...approvalParams,
        travellerManagers,
      };
    }

    if (policyResolvedId && usersCache) {
      const guestsFormIndexes = Array.from({
        length: cbt.passengersForm.guests,
      }).map((_o, i) => `${i + cbt.passengersForm.travellers.length}`);

      const guests = guestsFormIndexes.map((idx) => {
        const guest = {
          title:
            bookingFormFields[`${idx}-${getPassengerFieldName(isHotel)}_title`]
              ?.value,
          email:
            bookingFormFields[`${idx}-${getPassengerFieldName(isHotel)}_email`]
              ?.value, // cbt_
          telephone: bookingFormFields[
            `${idx}-${getPassengerFieldName(isHotel)}_phone_country`
          ]
            ? bookingFormFields[
                `${idx}-${getPassengerFieldName(isHotel)}_phone_country`
              ].value
                .split(" ")
                .join("")
            : "",
          firstName:
            bookingFormFields[
              `${idx}-${getPassengerFieldName(isHotel)}_firstname`
            ].value,
          lastName:
            bookingFormFields[
              `${idx}-${getPassengerFieldName(isHotel)}_lastname`
            ].value,
        };

        const gradeId =
          bookingFormFields[`${idx}-${getPassengerFieldName(isHotel)}_grade`]
            ?.value;

        if (gradeId !== undefined) {
          guest.gradeId = gradeId;
        }

        return guest;
      });

      approvalParams = {
        ...approvalParams,
        guests,
        travelArrangerEmail: customerUsername.split("---")[
          customerUsername.split("---").length - 1
        ],
      };

      approvalParams = bookingFormFields["cbt-reason-code"]
        ? {
            ...approvalParams,
            reasonCodeId: bookingFormFields["cbt-reason-code"].value,
          }
        : approvalParams;
    }

    const parameterGroup = buildBooking(
      bookingForm.acquisitionParamsBookingForm,
      { approvalParams, fields: bookingFormFields, isHotel }
    );

    const refsResolved: string[] = [
      ...getArrayRefsFromFlatRefs(refs),
      ...actualFlightServices,
    ];
    const refsResolvedFiltered = refsResolved.filter((ref) => ref !== null);

    return {
      TicketingCombination: getTicketingCombination({
        ticketingCombinations,
        paymentTypeId,
      }),
      BookReservationsWithParameters: {
        BookReservationWithParameters: {
          ExternalReservationId: "123456",
          PricedReference: {
            Price: fullPrice,
            Reference: refsResolvedFiltered.map((ref) => ({
              $t: ref.split(" ").join("+"),
            })),
          },
          ...(promoCode ? { PromoCode: { $t: promoCode } } : {}),
          Parameters: {
            ParameterGroup: parameterGroup,
          },
        },
      },
    };
  }

  function validateAllFields() {
    const fieldsToValidate = getFieldsToValidate();

    let allPassed = true;

    fieldsToValidate.forEach((elementName) => {
      const field = bookingFormFields[elementName];
      const passedField = validateField(field, {
        currentLanguage,
        bookingFormFields,
      });

      if (passedField.isError) {
        allPassed = false;
      }
    });

    return allPassed;
  }

  function getFieldsToValidate() {
    const allVisibleFields = bookingFormSections.reduce((acc, u) => {
      return acc.concat(u.elements);
    }, []);

    const result = Object.keys(bookingFormFields).filter((key) =>
      allVisibleFields.includes(key)
    );

    return result;
  }

  async function onUnconfirmedReservationSubmit() {
    setUnconfirmedReservations(null);
    dispatch(startUniversalLoading());

    const response = await bookReservationsConfirmRequest1(bookingResponse);

    if (response.success) {
      return await finishReservation(response.data, response.currency);
    }

    if (!response.success && response.errorMsg) {
      setIsSendingBookingForm(false);
      dispatch(stopUniversalLoading());
      return showCustomError({
        messageText: response.errorMsg,
        onClose: () => onCustomErrorClose(response.errorCode),
      });
    }
    setIsSendingBookingForm(false);
  }

  function getTypePayment(bookingData = null) {
    const currentPaymentMethod = (
      bookingData?.BookedReservations?.BookedReservation[0]?.PaymentFormMethods
        ?.paymentFormMethods ?? paymentFormMethods
    )?.find(
      (paymentMethod) =>
        paymentMethod.Id ===
        (bookingData?.BookedReservations?.BookedReservation[0]
          ?.TicketingCombination?.PaymentformOption?.Id ?? paymentTypeId)
    );

    if (currentPaymentMethod.Type === "offline") {
      return "OFFLINE";
    }
    return "ONLINE";
  }

  return {
    isConditionsConfirmed,
    toggleIsConditionsConfirmed,
    onSubmit,
    isConditionsError: !isConditionsConfirmed && showValidationErrors,
    unconfirmedReservations,
    summaryPrices,
    alternativeCurrencySummaryPrices,
    currency: currency ?? currencyState,
    alternativeCurrency,
    onUnconfirmedReservationSubmit,
    isSendingBookingForm,
    isFetchingServices,
  };
}

function getTicketingCombination({ ticketingCombinations, paymentTypeId }) {
  const ticketingCombination = ticketingCombinations.find(
    (el) => el.PaymentformOption.Id === paymentTypeId && el.TicketplaceOption.Id
  );

  return {
    TicketplaceOption: { ...ticketingCombination.TicketplaceOption },
    PaymentformOption: { ...ticketingCombination.PaymentformOption },
  };
}

function buildBooking(
  obj,
  { parentIndex = null, parentKey = null, approvalParams, fields, isHotel }
) {
  const newObj: any = {};
  let index = parentIndex;

  try {
    if (Array.isArray(obj)) {
      obj.forEach((o, i) => {
        index = getIndex(o, index);
        newObj[i] = {
          ...buildBooking(o, {
            parentIndex: index,
            parentKey,
            approvalParams,
            fields,
            isHotel,
          }),
        };
      });
      return Object.values(newObj);
    }

    Object.keys(obj).forEach((key) => {
      if (
        [
          "BookingFormGroup",
          "AdditionalInfo",
          "Info",
          "BookingFormElement",
        ].includes(key) &&
        (parentKey !== "BookingFormElement" || key !== "AdditionalInfo")
      ) {
        index = getIndex(obj[key], index);

        const bookingElement = buildBooking(obj[key], {
          parentIndex: index,
          parentKey: key,
          approvalParams,
          fields,
          isHotel,
        });

        newObj[BOOKING_MAP[key]] = bookingElement;

        if (
          bookingElement.Name === "approval_params" &&
          bookingElement.$t === "{}"
          // eslint-disable-next-line no-empty
        ) {
        }
      } else if (key === "Name") {
        const fieldName = index ? `${index}-${obj.Name}` : obj.Name;
        const field = fields[fieldName];
        const value = field
          ? typeof field.value === "object" && field.value !== null
            ? field.value.value
            : field.value
          : "";
        newObj.Name = obj.Name;
        if (obj.Name === "approval_params") {
          newObj.$t = JSON.stringify(approvalParams);
        } else {
          newObj.$t = value;
        }
      } else if (["Code", "Format", "$t", "Index"].includes(key)) {
        newObj[key] = obj[key];
      }
    });

    // make sure country code doesnt include whole phonenumber
    // if (newObj?.Name?.includes("_phone_country")) {
    if (newObj?.Name?.includes("_phone_")) {
      newObj.$t = getPhoneFieldValueFromBookingFormField(
        newObj,
        fields,
        parentIndex
      );
    }

    // if only @ is sent in passenger_email field, delete it
    if (newObj?.Name?.includes("_email") && newObj.$t === "@") {
      newObj.$t = "";
    }

    if (newObj?.Name?.includes("_title")) {
      newObj.$t = capitalizeFirstLetter(newObj.$t); // todo maybe back
    }

    if (newObj?.Name?.endsWith("passenger_frequent_flyer")) {
      const frequentFlyerFields = Object.values(fields).find(
        (el) =>
          el.name.includes("passenger_frequent_flyer") && el.value === newObj.$t
      );

      if (fields[`${frequentFlyerFields?.name}_number`]?.value === "") {
        newObj.$t = "";
      }
    }

    if (newObj?.Name?.endsWith("passenger_frequent_flyer_number")) {
      const frequentFlyerNumberFields = Object.values(fields).find(
        (el) =>
          el.name.includes("passenger_frequent_flyer") && el.value === newObj.$t
      );

      if (
        fields[frequentFlyerNumberFields?.name?.replace(/_number$/, "")]
          ?.value === ""
      ) {
        newObj.$t = "";
      }
    }

    if (
      newObj?.Name?.includes(
        `${getPassengerFieldName(isHotel)}_phone_number`
      ) &&
      newObj.$t.startsWith("+")
    ) {
      newObj.$t = "";
    }

    return newObj;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
}

function getPhoneFieldValueFromBookingFormField(
  fieldObject,
  fields,
  parentIndex
) {
  if (fieldObject?.Name?.includes("_phone_country")) {
    return getCountryCodeFromTelephoneNumber(fieldObject.$t).countryCode;
  }
  if (fieldObject?.Name?.includes("_phone_number")) {
    const keyObject =
      (parentIndex ? `${parentIndex}-` : "") +
      fieldObject.Name.split("_number").join("_country");

    const { value } = fields[keyObject];

    return getCountryCodeFromTelephoneNumber(value).phoneNumber;
  }
}

function isHotelReservation(bookingData) {
  return (
    bookingData.BookedReservations?.BookedReservation[0]?.RoomCategories !==
      undefined ?? bookingData.RoomCategories !== undefined
  );
}

function getPassengerFieldName(isHotel) {
  return isHotel ? "traveler" : "passenger";
}
